// This file contains helper functions for font family functionality
// The main font family logic is now in FontFamilyCommand.js

import { $getSelection, $isRangeSelection } from 'lexical';
import { $isFontFamilyNode } from './FontFamilyNode';

/**
 * Check if the current selection contains any font family styled text
 */
export function hasFontFamilyInSelection() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  const nodes = selection.getNodes();
  return nodes.some($isFontFamilyNode);
}

/**
 * Get all font families in the current selection
 */
export function getFontFamiliesInSelection() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return [];
  }

  const nodes = selection.getNodes();
  const fontFamilyNodes = nodes.filter($isFontFamilyNode);
  return fontFamilyNodes.map((node) => node.getFontFamily());
}

/**
 * Check if the current selection has a specific font family
 */
export function hasSpecificFontFamilyInSelection(fontFamily) {
  const fontFamilies = getFontFamiliesInSelection();
  return fontFamilies.includes(fontFamily);
}

/**
 * Get the most common font family in the current selection
 */
export function getMostCommonFontFamilyInSelection() {
  const fontFamilies = getFontFamiliesInSelection();
  if (fontFamilies.length === 0) {
    return null;
  }

  // Count occurrences of each font family
  const counts = {};
  fontFamilies.forEach((family) => {
    counts[family] = (counts[family] || 0) + 1;
  });

  // Find the most common one
  return Object.keys(counts).reduce((a, b) => (counts[a] > counts[b] ? a : b));
}

/**
 * Get font family from DOM styles (fallback method)
 */
export function getFontFamilyFromDOM() {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const container = range.commonAncestorContainer;

  // Walk up the DOM tree to find the first element with font-family style
  let element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;

  while (element && element !== document.body) {
    const computedStyle = window.getComputedStyle(element);
    const { fontFamily } = computedStyle;

    if (
      fontFamily &&
      fontFamily !== 'inherit' &&
      fontFamily !== 'initial' &&
      fontFamily !== 'serif' &&
      fontFamily !== 'sans-serif' &&
      fontFamily !== 'monospace'
    ) {
      // Extract the first font family from the font stack
      const firstFont = fontFamily.split(',')[0].replace(/['"]/g, '').trim();

      // Map common font families to our dropdown options
      const fontMap = {
        'Arial, Helvetica, sans-serif': 'Arial',
        'Helvetica, Arial, sans-serif': 'Helvetica',
        'Times New Roman, Times, serif': 'Times New Roman',
        'Georgia, serif': 'Georgia',
        'Verdana, Geneva, sans-serif': 'Verdana',
        'Courier New, Courier, monospace': 'Courier New',
        'Comic Sans MS, cursive': 'Comic Sans MS',
        'Impact, Charcoal, sans-serif': 'Impact',
        'Trebuchet MS, Helvetica, sans-serif': 'Trebuchet MS',
        'Palatino, Book Antiqua, serif': 'Palatino',
        'Garamond, serif': 'Garamond',
        'Bookman, serif': 'Bookman',
        'Avant Garde, sans-serif': 'Avant Garde',
        'Helvetica Neue, Helvetica, Arial, sans-serif': 'Helvetica Neue',
        'Calibri, Candara, Segoe, sans-serif': 'Calibri',
        'Cambria, Georgia, serif': 'Cambria',
        'Tahoma, Geneva, sans-serif': 'Tahoma',
        'Lucida Console, Monaco, monospace': 'Lucida Console',
        'Monaco, monospace': 'Monaco',
        'Consolas, monospace': 'Consolas',
      };

      // Check if the font family matches any of our known fonts
      const matchingFont = Object.entries(fontMap).find(([, fontName]) =>
        fontFamily.includes(fontName)
      );
      if (matchingFont) {
        return matchingFont[1];
      }

      // If no match found, return the first font
      return firstFont;
    }

    element = element.parentElement;
  }

  return null;
}

/**
 * Get unique font families in the current selection
 */
export function getUniqueFontFamiliesInSelection() {
  const fontFamilies = getFontFamiliesInSelection();
  return [...new Set(fontFamilies)];
}
